import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { apiErrorMessage } from '../apiError';
import ActivityLog from './ActivityLog';

const STATUS_COLORS = {
  submitted: '#888',
  under_review: '#c77700',
  approved: '#2e7d32',
  denied: '#cc0000',
  refile_requested: '#6a1b9a',
};

const STATUS_OPTIONS = [
  ['submitted', 'Submitted'],
  ['under_review', 'Under Review'],
  ['approved', 'Approved'],
  ['denied', 'Denied'],
  ['refile_requested', 'Refile Requested'],
];

// Colored like a status badge, but clickable: picking a value saves it immediately.
function StatusSelect({ status, onChange }) {
  return (
    <select
      value={status}
      onChange={(e) => onChange(e.target.value)}
      style={{
        padding: '3px 8px',
        borderRadius: '12px',
        border: 'none',
        fontSize: '12px',
        fontWeight: '600',
        color: '#fff',
        backgroundColor: STATUS_COLORS[status] || '#888',
        cursor: 'pointer',
      }}
    >
      {STATUS_OPTIONS.map(([value, label]) => (
        <option key={value} value={value} style={{ color: '#000', backgroundColor: '#fff' }}>{label}</option>
      ))}
    </select>
  );
}

const STATUS_LOOKUP = Object.fromEntries(STATUS_OPTIONS);

const MESSAGE_TYPES = [
  ['refile_request', 'Request a refile'],
  ['denial', 'Reason for denial'],
];
const MESSAGE_LABELS = Object.fromEntries(MESSAGE_TYPES);
// status that goes with each message type, and the message type to suggest for each status
const STATUS_FOR_MESSAGE = { refile_request: 'refile_requested', denial: 'denied' };
const MESSAGE_FOR_STATUS = { refile_requested: 'refile_request', denied: 'denial' };

// One-way email to the filer (sent from the noreply address), plus the history of what's been sent.
// apiPath is e.g. /admin/filings/<id>; onStatusChange is told the record's new status after a send.
function MessageFiler({ apiPath, token, filerEmail, presetType, onStatusChange }) {
  const [messages, setMessages] = useState([]);
  const [messageType, setMessageType] = useState(presetType || 'refile_request');
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_API_URL}${apiPath}/messages`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setMessages(res.data))
      .catch(() => setResult({ ok: false, text: 'Could not load message history.' }));
  }, [apiPath, token]);

  useEffect(() => {
    if (presetType) setMessageType(presetType);
  }, [presetType]);

  const handleSend = async () => {
    const newStatus = STATUS_LOOKUP[STATUS_FOR_MESSAGE[messageType]];
    if (!window.confirm(`Email this message to ${filerEmail}? The status will be set to "${newStatus}".`)) return;
    setSending(true);
    setResult(null);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}${apiPath}/messages`,
        { message_type: messageType, message: text },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessages(prev => [res.data.message, ...prev]);
      setText('');
      setResult({ ok: true, text: `Email sent to ${filerEmail}.` });
      onStatusChange(res.data.status);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setResult({ ok: false, text: typeof detail === 'string' ? detail : 'The email could not be sent. Please try again.' });
    } finally {
      setSending(false);
    }
  };

  const disabled = sending || !text.trim();

  return (
    <div>
      <p style={{ fontSize: '13px', color: '#666', marginTop: 0 }}>
        Sends a one-way email to <strong>{filerEmail}</strong> from noreply@matownforms.com. The filer can't reply. A refile request includes a link to start a new filing; this one stays marked Refile Requested, and you review the new one as usual.
      </p>
      <select style={{ padding: '8px 10px', borderRadius: '4px', border: '1px solid #ccc', fontSize: '14px', marginBottom: '8px' }}
        value={messageType} onChange={(e) => setMessageType(e.target.value)}>
        {MESSAGE_TYPES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
      </select>
      <textarea
        style={{ display: 'block', width: '100%', minHeight: '110px', padding: '10px', borderRadius: '4px', border: '1px solid #ccc', fontSize: '14px', fontFamily: 'inherit', boxSizing: 'border-box' }}
        placeholder={messageType === 'denial' ? 'Explain why this was denied...' : 'Explain what needs to be corrected or added in the new filing...'}
        value={text}
        maxLength={5000}
        onChange={(e) => setText(e.target.value)}
      />
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '8px' }}>
        <button
          style={{ padding: '8px 16px', backgroundColor: '#1a3a5c', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '14px', cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.6 : 1 }}
          disabled={disabled}
          onClick={handleSend}
        >
          {sending ? 'Sending...' : 'Send Email'}
        </button>
        {result && <span style={{ fontSize: '13px', color: result.ok ? '#2e7d32' : '#cc0000' }}>{result.text}</span>}
      </div>

      {messages.length > 0 && (
        <div style={{ marginTop: '20px' }}>
          <h4 style={{ color: '#1a3a5c', fontSize: '14px', margin: '0 0 8px' }}>Sent messages ({messages.length})</h4>
          {messages.map(m => (
            <div key={m.id} style={{ borderLeft: `4px solid ${STATUS_COLORS[STATUS_FOR_MESSAGE[m.message_type]] || '#888'}`, backgroundColor: '#f8fafc', padding: '10px 12px', marginBottom: '8px', borderRadius: '4px' }}>
              <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                <strong>{MESSAGE_LABELS[m.message_type] || m.message_type}</strong> — {m.sent_at ? new Date(m.sent_at).toLocaleString() : ''} — by {m.admin_email} to {m.sent_to}
              </div>
              <div style={{ fontSize: '14px', whiteSpace: 'pre-wrap' }}>{m.body}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AdminDashboard() {
  const [filings, setFilings] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterTown, setFilterTown] = useState('');
  const [filterYear, setFilterYear] = useState('');
  const [filterFormType, setFilterFormType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [searchName, setSearchName] = useState('');
  const [selectedFiling, setSelectedFiling] = useState(null);
  const [filingDetail, setFilingDetail] = useState(null);
  const [activeTab, setActiveTab] = useState('filings');
  const [towns, setTowns] = useState([]);
  const [townsLoading, setTownsLoading] = useState(false);
  const [townSearch, setTownSearch] = useState('');
  const [editingTown, setEditingTown] = useState(null);
  const [editTownData, setEditTownData] = useState({});
  const [townSaveMessage, setTownSaveMessage] = useState('');
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [promotingUser, setPromotingUser] = useState(null);
  const [promoteData, setPromoteData] = useState({ town_code: '', password: '' });
  const [promoteMessage, setPromoteMessage] = useState('');
  const [excise, setExcise] = useState([]);
  const [exciseLoading, setExciseLoading] = useState(false);
  const [exciseFilterStatus, setExciseFilterStatus] = useState('');
  const [exciseFilterTown, setExciseFilterTown] = useState('');
  const [selectedExcise, setSelectedExcise] = useState(null);
  const [exciseDetail, setExciseDetail] = useState(null);
  const [messagePreset, setMessagePreset] = useState(null);

  const token = localStorage.getItem('adminToken');
  const role = localStorage.getItem('adminRole');
  const isSuperAdmin = role === 'super_admin';

  const currentYear = new Date().getFullYear();
  const yearOptions = [currentYear, currentYear - 1, currentYear - 2, currentYear - 3];

  useEffect(() => {
    if (!token) {
      window.location.href = '/login';
      return;
    }
    fetchStats();
    fetchFilings();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (activeTab === 'towns' && isSuperAdmin && towns.length === 0) {
      fetchTowns();
    }
    if (activeTab === 'users' && isSuperAdmin && users.length === 0) {
      fetchUsers();
    }
    if (activeTab === 'excise' && excise.length === 0) {
      fetchExcise();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStats(response.data);
    } catch (err) {
      if (err.response?.status === 401) window.location.href = '/login';
    }
  };

  const fetchFilings = async (town, year, formType, status) => {
    setLoading(true);
    try {
      const params = {};
      if (town) params.town_code = town;
      if (year) params.fiscal_year = year;
      if (formType) params.form_type = formType;
      if (status) params.status = status;
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/filings`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      setFilings(response.data);
    } catch (err) {
      setError('Failed to load filings.');
    } finally {
      setLoading(false);
    }
  };

  const fetchTowns = async () => {
    setTownsLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/towns`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTowns(response.data);
    } catch (err) {
      setError('Failed to load towns.');
    } finally {
      setTownsLoading(false);
    }
  };

  const fetchUsers = async () => {
    setUsersLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsers(response.data);
    } catch (err) {
      setError('Failed to load users.');
    } finally {
      setUsersLoading(false);
    }
  };

  const fetchExcise = async (status, town) => {
    setExciseLoading(true);
    try {
      const params = {};
      if (status) params.status = status;
      if (town) params.town_code = town;
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/excise`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      setExcise(response.data);
    } catch (err) {
      setError('Failed to load excise abatements.');
    } finally {
      setExciseLoading(false);
    }
  };

  const handleExciseFilter = () => {
    fetchExcise(exciseFilterStatus, exciseFilterTown);
  };

  const handleExciseClearFilters = () => {
    setExciseFilterStatus('');
    setExciseFilterTown('');
    fetchExcise();
  };

  const handleViewExcise = async (abatementId) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/excise/${abatementId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMessagePreset(null);
      setExciseDetail(response.data);
      setSelectedExcise(abatementId);
    } catch (err) {
      setError('Failed to load abatement detail.');
    }
  };

  const handleUpdateExciseStatus = async (abatementId, newStatus) => {
    try {
      const response = await axios.patch(
        `${process.env.REACT_APP_API_URL}/admin/excise/${abatementId}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setExcise(excise.map(a => a.id === abatementId ? response.data : a));
      setExciseDetail(prev => prev ? { ...prev, abatement: response.data } : prev);
    } catch (err) {
      setError('Failed to update status.');
    }
  };

  const handleDeleteExcise = async (abatementId, filingReference) => {
    if (!window.confirm(`Are you sure you want to delete excise abatement ${filingReference || abatementId}? This cannot be undone.`)) {
      return;
    }
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/admin/excise/${abatementId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setExcise(excise.filter(a => a.id !== abatementId));
      if (selectedExcise === abatementId) {
        setSelectedExcise(null);
        setExciseDetail(null);
      }
    } catch (err) {
      setError('Failed to delete excise abatement.');
    }
  };

  const handleDownloadDocument = async (abatementId, documentId, filename) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/admin/excise/${abatementId}/documents/${documentId}/download`,
        { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Failed to download document.');
    }
  };

  const handleFilter = () => {
    fetchFilings(filterTown, filterYear, filterFormType, filterStatus);
  };

  const handleClearFilters = () => {
    setFilterTown('');
    setFilterYear('');
    setFilterFormType('');
    setFilterStatus('');
    setSearchName('');
    fetchFilings();
  };

  const handleUpdateFilingStatus = async (filingId, newStatus) => {
    try {
      const response = await axios.patch(
        `${process.env.REACT_APP_API_URL}/admin/filings/${filingId}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setFilings(filings.map(f => f.id === filingId ? response.data : f));
      setFilingDetail(prev => prev ? { ...prev, filing: { ...prev.filing, status: response.data.status } } : prev);
    } catch (err) {
      setError('Failed to update status.');
    }
  };

  // From a table row: change status, then offer to open the detail page with the message box ready
  const handleFilingRowStatus = async (filingId, newStatus) => {
    await handleUpdateFilingStatus(filingId, newStatus);
    if (MESSAGE_FOR_STATUS[newStatus] && window.confirm('Do you want to email the filer an explanation?')) {
      await handleViewFiling(filingId);
      setMessagePreset(MESSAGE_FOR_STATUS[newStatus]);
    }
  };

  const handleExciseRowStatus = async (abatementId, newStatus) => {
    await handleUpdateExciseStatus(abatementId, newStatus);
    if (MESSAGE_FOR_STATUS[newStatus] && window.confirm('Do you want to email the filer an explanation?')) {
      await handleViewExcise(abatementId);
      setMessagePreset(MESSAGE_FOR_STATUS[newStatus]);
    }
  };

  // After a message is sent the backend has already changed the status; mirror it in the list + detail
  const applyFilingStatus = (filingId, newStatus) => {
    setFilings(prev => prev.map(f => f.id === filingId ? { ...f, status: newStatus } : f));
    setFilingDetail(prev => prev ? { ...prev, filing: { ...prev.filing, status: newStatus } } : prev);
  };

  const applyExciseStatus = (abatementId, newStatus) => {
    setExcise(prev => prev.map(a => a.id === abatementId ? { ...a, status: newStatus } : a));
    setExciseDetail(prev => prev ? { ...prev, abatement: { ...prev.abatement, status: newStatus } } : prev);
  };

  const handleViewFiling = async (filingId) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/filings/${filingId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMessagePreset(null);
      setFilingDetail(response.data);
      setSelectedFiling(filingId);
    } catch (err) {
      setError('Failed to load filing detail.');
    }
  };

  // Opens the printable filing page in a new tab; print=1 makes it open the print dialog automatically.
  const handlePrintFiling = (filingId) => {
    window.open(`/filing-view?id=${filingId}&admin=1&print=1`, '_blank');
  };

  const handleDeleteFiling = async (filingId, filingReference) => {
    if (!window.confirm(`Are you sure you want to delete filing ${filingReference || filingId}? This cannot be undone.`)) {
      return;
    }
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/admin/filings/${filingId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setFilings(filings.filter(f => f.id !== filingId));
      if (selectedFiling === filingId) {
        setSelectedFiling(null);
        setFilingDetail(null);
      }
    } catch (err) {
      setError('Failed to delete filing.');
    }
  };

  const handleDeleteUser = async (userId, userEmail) => {
    if (!window.confirm(`Delete the account for ${userEmail}? Their filings stay on record.`)) {
      return;
    }
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsers(users.filter(u => u.id !== userId));
    } catch (err) {
      setError('Failed to delete user.');
    }
  };

  const handlePromoteUser = async (userId) => {
    if (!promoteData.town_code || !promoteData.password) {
      setError('Town code and password are required.');
      return;
    }
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/admin/users/${userId}/promote`,
        promoteData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPromotingUser(null);
      setPromoteData({ town_code: '', password: '' });
      setPromoteMessage(response.data.message);
      setTimeout(() => setPromoteMessage(''), 4000);
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to promote user.'));
    }
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (filterTown) params.append('town_code', filterTown);
      if (filterYear) params.append('fiscal_year', filterYear);
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/export?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'filings.xlsx');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Export failed. Please try again.');
    }
  };

  const handleToggleTownActive = async (town) => {
    try {
      await axios.patch(
        `${process.env.REACT_APP_API_URL}/admin/towns/${town.town_code}`,
        { is_active: !town.is_active },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTowns(towns.map(t => t.town_code === town.town_code ? { ...t, is_active: !town.is_active } : t));
    } catch (err) {
      setError('Failed to update town.');
    }
  };

  const handleEditTown = (town) => {
    setEditingTown(town.town_code);
    setEditTownData({
      assessor_email: town.assessor_email || '',
      assessor_phone: town.assessor_phone || '',
      assessor_website: town.assessor_website || '',
    });
  };

  const handleSaveTown = async (townCode) => {
    try {
      await axios.patch(
        `${process.env.REACT_APP_API_URL}/admin/towns/${townCode}`,
        editTownData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTowns(towns.map(t => t.town_code === townCode ? { ...t, ...editTownData } : t));
      setEditingTown(null);
      setTownSaveMessage('Saved!');
      setTimeout(() => setTownSaveMessage(''), 2000);
    } catch (err) {
      setError('Failed to save town.');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminRole');
    window.location.href = '/login';
  };

  const filteredFilings = filings.filter(f =>
    searchName === '' ||
    f.owner_name.toLowerCase().includes(searchName.toLowerCase()) ||
    (f.business_name && f.business_name.toLowerCase().includes(searchName.toLowerCase()))
  );

  const filteredTowns = towns.filter(t =>
    townSearch === '' || t.town_name.toLowerCase().includes(townSearch.toLowerCase())
  );

  const filteredUsers = users.filter(u =>
    userSearch === '' || u.email.toLowerCase().includes(userSearch.toLowerCase())
  );

  // --- Filing Detail View ---
  if (selectedFiling && filingDetail) {
    const { filing, reported_items, signature } = filingDetail;
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.detailHeader}>
            <button style={styles.backButton} onClick={() => { setSelectedFiling(null); setFilingDetail(null); }}>
              ← Back to Dashboard
            </button>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h1 style={styles.title}>Filing Detail</h1>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  style={styles.printButton}
                  onClick={() => handlePrintFiling(filing.id)}
                >
                  Print / Save as PDF
                </button>
                <button
                  style={styles.deleteButton}
                  onClick={() => {
                    handleDeleteFiling(filing.id, filing.filing_reference);
                    setSelectedFiling(null);
                    setFilingDetail(null);
                  }}
                >
                  Delete Filing
                </button>
              </div>
            </div>
          </div>

          <div style={styles.detailSection}>
            <h3 style={styles.sectionTitle}>Filing Information</h3>
            <div style={styles.detailGrid}>
              <div style={styles.detailItem}><strong>Reference:</strong> {filing.filing_reference || 'N/A'}</div>
              <div style={styles.detailItem}><strong>Form Type:</strong> {filing.form_type === 'form_2' ? 'Form 2 — Business' : 'Form 2HF — Second Home'}</div>
              <div style={styles.detailItem}><strong>Town:</strong> {filing.town_or_city} ({filing.town_code})</div>
              <div style={styles.detailItem}><strong>Fiscal Year:</strong> {filing.fiscal_year}</div>
              <div style={styles.detailItem}><strong>Submitted:</strong> {filing.submitted_at ? new Date(filing.submitted_at).toLocaleString() : 'N/A'}</div>
            </div>
          </div>

          <div style={styles.detailSection}>
            <h3 style={styles.sectionTitle}>Status</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <StatusSelect status={filing.status} onChange={(s) => { handleUpdateFilingStatus(filing.id, s); if (MESSAGE_FOR_STATUS[s]) setMessagePreset(MESSAGE_FOR_STATUS[s]); }} />
            </div>
          </div>

          <div style={styles.detailSection}>
            <h3 style={styles.sectionTitle}>Message Filer</h3>
            <MessageFiler
              apiPath={`/admin/filings/${filing.id}`}
              token={token}
              filerEmail={filing.email}
              presetType={messagePreset}
              onStatusChange={(s) => applyFilingStatus(filing.id, s)}
            />
          </div>

          <div style={styles.detailSection}>
            <h3 style={styles.sectionTitle}>Filer Information</h3>
            <div style={styles.detailGrid}>
              <div style={styles.detailItem}><strong>Owner Name:</strong> {filing.owner_name}</div>
              {filing.business_name && <div style={styles.detailItem}><strong>Business:</strong> {filing.business_name}</div>}
              <div style={styles.detailItem}><strong>Email:</strong> {filing.email}</div>
              <div style={styles.detailItem}><strong>Phone:</strong> {filing.phone}</div>
            </div>
          </div>

          <div style={styles.detailSection}>
            <h3 style={styles.sectionTitle}>Reported Items ({reported_items.length})</h3>
            {reported_items.map((item, i) => (
              <div key={i} style={styles.itemRow}>
                <strong>Schedule {item.schedule}</strong> — {item.description}
                {item.purchase_price && ` — $${item.purchase_price}`}
                <span style={styles.itemBadge}>{item.own_or_other}</span>
              </div>
            ))}
          </div>

          {signature && (
            <div style={styles.detailSection}>
              <h3 style={styles.sectionTitle}>E-Signature</h3>
              <div style={styles.detailGrid}>
                <div style={styles.detailItem}><strong>Signer:</strong> {signature.signer_full_name}</div>
                <div style={styles.detailItem}><strong>Title:</strong> {signature.signer_title}</div>
                <div style={styles.detailItem}><strong>Signed At:</strong> {signature.signed_at ? new Date(signature.signed_at).toLocaleString() : 'N/A'}</div>
                <div style={styles.detailItem}><strong>Token:</strong> {signature.signature_token}</div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // --- Excise Abatement Detail View ---
  if (selectedExcise && exciseDetail) {
    const { abatement, documents } = exciseDetail;
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.detailHeader}>
            <button style={styles.backButton} onClick={() => { setSelectedExcise(null); setExciseDetail(null); }}>
              ← Back to Dashboard
            </button>
            <h1 style={styles.title}>Excise Abatement Detail</h1>
          </div>

          <div style={styles.detailSection}>
            <h3 style={styles.sectionTitle}>Filing Information</h3>
            <div style={styles.detailGrid}>
              <div style={styles.detailItem}><strong>Reference:</strong> {abatement.filing_reference || 'N/A'}</div>
              <div style={styles.detailItem}><strong>Town Code:</strong> {abatement.town_code}</div>
              <div style={styles.detailItem}><strong>Bill Number:</strong> {abatement.bill_number}</div>
              <div style={styles.detailItem}><strong>Bill Year:</strong> {abatement.bill_year}</div>
              <div style={styles.detailItem}><strong>Plate Number:</strong> {abatement.plate_number}</div>
              <div style={styles.detailItem}><strong>Reason:</strong> {abatement.reason}</div>
              {exciseDetail.other_reason_detail && <div style={styles.detailItem}><strong>Reason Detail:</strong> {exciseDetail.other_reason_detail}</div>}
              <div style={styles.detailItem}><strong>Event Date:</strong> {exciseDetail.event_date || 'N/A'}</div>
              <div style={styles.detailItem}><strong>Submitted:</strong> {abatement.submitted_at ? new Date(abatement.submitted_at).toLocaleString() : 'N/A'}</div>
            </div>
          </div>

          <div style={styles.detailSection}>
            <h3 style={styles.sectionTitle}>Filer Information</h3>
            <div style={styles.detailGrid}>
              <div style={styles.detailItem}><strong>Owner Name:</strong> {abatement.owner_name}</div>
              <div style={styles.detailItem}><strong>Address on Bill:</strong> {exciseDetail.address_on_bill}</div>
              {exciseDetail.mailing_address && <div style={styles.detailItem}><strong>Mailing Address:</strong> {exciseDetail.mailing_address}</div>}
              <div style={styles.detailItem}><strong>Phone:</strong> {exciseDetail.phone}</div>
              <div style={styles.detailItem}><strong>Email:</strong> {exciseDetail.email}</div>
            </div>
          </div>

          <div style={styles.detailSection}>
            <h3 style={styles.sectionTitle}>E-Signature</h3>
            <div style={styles.detailGrid}>
              <div style={styles.detailItem}><strong>Signer:</strong> {exciseDetail.signature_name}</div>
              <div style={styles.detailItem}><strong>Signed At:</strong> {exciseDetail.signed_at ? new Date(exciseDetail.signed_at).toLocaleString() : 'N/A'}</div>
            </div>
          </div>

          <div style={styles.detailSection}>
            <h3 style={styles.sectionTitle}>Supporting Documents ({documents.length})</h3>
            {documents.length === 0 ? (
              <p style={{ fontSize: '14px', color: '#888' }}>No documents uploaded.</p>
            ) : documents.map(doc => (
              <div key={doc.id} style={styles.itemRow}>
                📄 {doc.filename} {doc.file_size ? `(${Math.round(doc.file_size / 1024)} KB)` : ''}
                <button style={{ ...styles.viewButton, marginLeft: 'auto' }} onClick={() => handleDownloadDocument(abatement.id, doc.id, doc.filename)}>Download</button>
              </div>
            ))}
          </div>

          <div style={styles.detailSection}>
            <h3 style={styles.sectionTitle}>Status</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <StatusSelect status={abatement.status} onChange={(s) => { handleUpdateExciseStatus(abatement.id, s); if (MESSAGE_FOR_STATUS[s]) setMessagePreset(MESSAGE_FOR_STATUS[s]); }} />
            </div>
          </div>

          <div style={styles.detailSection}>
            <h3 style={styles.sectionTitle}>Message Filer</h3>
            <MessageFiler
              apiPath={`/admin/excise/${abatement.id}`}
              token={token}
              filerEmail={exciseDetail.email}
              presetType={messagePreset}
              onStatusChange={(s) => applyExciseStatus(abatement.id, s)}
            />
          </div>
        </div>
      </div>
    );
  }

  // --- Main Dashboard ---
  return (
    <div style={styles.container}>
      <div style={styles.card}>

        <div style={styles.header}>
          <h1 style={styles.title}>
            {isSuperAdmin ? 'Admin Dashboard' : `${stats?.town_code || ''} Town Dashboard`}
          </h1>
          <button style={styles.logoutButton} onClick={handleLogout}>Sign Out</button>
        </div>

        {/* Tabs */}
        <div style={styles.tabRow}>
          <button style={activeTab === 'filings' ? styles.tabActive : styles.tab} onClick={() => setActiveTab('filings')}>Filings</button>
          <button style={activeTab === 'excise' ? styles.tabActive : styles.tab} onClick={() => setActiveTab('excise')}>Excise Abatements</button>
          {isSuperAdmin && (
            <>
              <button style={activeTab === 'towns' ? styles.tabActive : styles.tab} onClick={() => setActiveTab('towns')}>Town Management</button>
              <button style={activeTab === 'users' ? styles.tabActive : styles.tab} onClick={() => setActiveTab('users')}>User Management</button>
              <button style={activeTab === 'activity' ? styles.tabActive : styles.tab} onClick={() => setActiveTab('activity')}>Activity</button>
            </>
          )}
        </div>

        {/* --- FILINGS TAB --- */}
        {activeTab === 'filings' && (
          <>
            {stats && (
              <div style={styles.statsRow}>
                <div style={styles.statBox}>
                  <div style={styles.statNumber}>{stats.total_filings}</div>
                  <div style={styles.statLabel}>Total Filings</div>
                </div>
                {stats.total_users !== undefined && (
                  <div style={styles.statBox}>
                    <div style={styles.statNumber}>{stats.total_users}</div>
                    <div style={styles.statLabel}>Registered Users</div>
                  </div>
                )}
                {stats.towns_with_filings !== undefined && (
                  <div style={styles.statBox}>
                    <div style={styles.statNumber}>{stats.towns_with_filings}</div>
                    <div style={styles.statLabel}>Towns with Filings</div>
                  </div>
                )}
                {stats.by_form_type && (
                  <>
                    <div style={styles.statBox}>
                      <div style={styles.statNumber}>{stats.by_form_type.form_2}</div>
                      <div style={styles.statLabel}>Form 2 Filings</div>
                    </div>
                    <div style={styles.statBox}>
                      <div style={styles.statNumber}>{stats.by_form_type.form_2hf}</div>
                      <div style={styles.statLabel}>Form 2HF Filings</div>
                    </div>
                  </>
                )}
              </div>
            )}

            <div style={styles.filterRow}>
              <input style={styles.filterInput} type="text" placeholder="Search by name..." value={searchName} onChange={(e) => setSearchName(e.target.value)} />
              {isSuperAdmin && (
                <input style={styles.filterInput} type="text" placeholder="Town code (e.g. 035)" value={filterTown} onChange={(e) => setFilterTown(e.target.value)} />
              )}
              <select style={styles.filterInput} value={filterYear} onChange={(e) => setFilterYear(e.target.value)}>
                <option value="">All years</option>
                {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
              <select style={styles.filterInput} value={filterFormType} onChange={(e) => setFilterFormType(e.target.value)}>
                <option value="">All form types</option>
                <option value="form_2">Form 2</option>
                <option value="form_2hf">Form 2HF</option>
              </select>
              <select style={styles.filterInput} value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                <option value="">All statuses</option>
                <option value="submitted">Submitted</option>
                <option value="under_review">Under Review</option>
                <option value="approved">Approved</option>
                <option value="denied">Denied</option>
                <option value="refile_requested">Refile Requested</option>
              </select>
              <button style={styles.filterButton} onClick={handleFilter}>Filter</button>
              <button style={styles.clearButton} onClick={handleClearFilters}>Clear</button>
              <button style={styles.exportButton} onClick={handleExport}>Export to Excel</button>
            </div>

            {error && <p style={styles.error}>{error}</p>}

            {loading ? (
              <p style={styles.loading}>Loading filings...</p>
            ) : filteredFilings.length === 0 ? (
              <p style={styles.loading}>No filings found.</p>
            ) : (
              <table style={styles.table}>
                <thead>
                  <tr>
                    {['Reference', 'Form Type', 'Town Code', 'Town', 'Year', 'Owner / Business', 'Email', 'Status', 'Submitted', ''].map(h => (
                      <th key={h} style={styles.th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredFilings.map((filing, i) => (
                    <tr key={filing.id} style={{ backgroundColor: i % 2 === 0 ? '#f8fafc' : '#ffffff' }}>
                      <td style={styles.td}>{filing.filing_reference || '—'}</td>
                      <td style={styles.td}>{filing.form_type === 'form_2' ? 'Form 2' : 'Form 2HF'}</td>
                      <td style={styles.td}>{filing.town_code}</td>
                      <td style={styles.td}>{filing.town_or_city}</td>
                      <td style={styles.td}>{filing.fiscal_year}</td>
                      <td style={styles.td}>{filing.business_name || filing.owner_name}</td>
                      <td style={styles.td}>{filing.email}</td>
                      <td style={styles.td}><StatusSelect status={filing.status} onChange={(s) => handleFilingRowStatus(filing.id, s)} /></td>
                      <td style={styles.td}>{filing.submitted_at ? new Date(filing.submitted_at).toLocaleDateString() : '—'}</td>
                      <td style={styles.td}>
                        <button style={styles.viewButton} onClick={() => handleViewFiling(filing.id)}>View</button>
                        <button style={styles.deleteSmallButton} onClick={() => handleDeleteFiling(filing.id, filing.filing_reference)}>Delete</button>
                        <div style={{ marginTop: '4px' }}>
                          <button style={styles.viewButton} onClick={() => handlePrintFiling(filing.id)}>Print</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}

        {/* --- EXCISE ABATEMENTS TAB --- */}
        {activeTab === 'excise' && (
          <>
            <div style={styles.filterRow}>
              {isSuperAdmin && (
                <input style={styles.filterInput} type="text" placeholder="Town code (e.g. 035)" value={exciseFilterTown} onChange={(e) => setExciseFilterTown(e.target.value)} />
              )}
              <select style={styles.filterInput} value={exciseFilterStatus} onChange={(e) => setExciseFilterStatus(e.target.value)}>
                <option value="">All statuses</option>
                <option value="submitted">Submitted</option>
                <option value="under_review">Under Review</option>
                <option value="approved">Approved</option>
                <option value="denied">Denied</option>
                <option value="refile_requested">Refile Requested</option>
              </select>
              <button style={styles.filterButton} onClick={handleExciseFilter}>Filter</button>
              <button style={styles.clearButton} onClick={handleExciseClearFilters}>Clear</button>
            </div>

            {error && <p style={styles.error}>{error}</p>}

            {exciseLoading ? (
              <p style={styles.loading}>Loading excise abatements...</p>
            ) : excise.length === 0 ? (
              <p style={styles.loading}>No excise abatements found.</p>
            ) : (
              <table style={styles.table}>
                <thead>
                  <tr>
                    {['Reference', 'Owner', 'Plate', 'Reason', ...(isSuperAdmin ? ['Town Code'] : []), 'Status', 'Submitted', ''].map(h => (
                      <th key={h} style={styles.th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {excise.map((a, i) => (
                    <tr key={a.id} style={{ backgroundColor: i % 2 === 0 ? '#f8fafc' : '#ffffff' }}>
                      <td style={styles.td}>{a.filing_reference || '—'}</td>
                      <td style={styles.td}>{a.owner_name}</td>
                      <td style={styles.td}>{a.plate_number}</td>
                      <td style={styles.td}>{a.reason}</td>
                      {isSuperAdmin && <td style={styles.td}>{a.town_code}</td>}
                      <td style={styles.td}><StatusSelect status={a.status} onChange={(s) => handleExciseRowStatus(a.id, s)} /></td>
                      <td style={styles.td}>{a.submitted_at ? new Date(a.submitted_at).toLocaleDateString() : '—'}</td>
                      <td style={styles.td}>
                        <button style={styles.viewButton} onClick={() => handleViewExcise(a.id)}>View</button>
                        <button style={styles.deleteSmallButton} onClick={() => handleDeleteExcise(a.id, a.filing_reference)}>Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}

        {/* --- TOWN MANAGEMENT TAB --- */}
        {activeTab === 'towns' && isSuperAdmin && (
          <>
            <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
              <input style={styles.filterInput} type="text" placeholder="Search towns..." value={townSearch} onChange={(e) => setTownSearch(e.target.value)} />
              {townSaveMessage && <span style={{ color: '#2e7d32', fontSize: '14px', fontWeight: '600' }}>{townSaveMessage}</span>}
            </div>
            {townsLoading ? (
              <p style={styles.loading}>Loading towns...</p>
            ) : (
              <table style={styles.table}>
                <thead>
                  <tr>
                    {['Town', 'Code', 'Status', 'Assessor Email', 'Assessor Phone', 'Website', ''].map(h => (
                      <th key={h} style={styles.th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredTowns.map((town, i) => (
                    <tr key={town.town_code} style={{ backgroundColor: i % 2 === 0 ? '#f8fafc' : '#ffffff' }}>
                      <td style={styles.td}>{town.town_name}</td>
                      <td style={styles.td}>{town.town_code}</td>
                      <td style={styles.td}>
                        <button style={town.is_active ? styles.activeToggle : styles.inactiveToggle} onClick={() => handleToggleTownActive(town)}>
                          {town.is_active ? 'Active' : 'Inactive'}
                        </button>
                      </td>
                      {editingTown === town.town_code ? (
                        <>
                          <td style={styles.td}><input style={styles.inlineInput} value={editTownData.assessor_email} onChange={(e) => setEditTownData({ ...editTownData, assessor_email: e.target.value })} /></td>
                          <td style={styles.td}><input style={styles.inlineInput} value={editTownData.assessor_phone} onChange={(e) => setEditTownData({ ...editTownData, assessor_phone: e.target.value })} /></td>
                          <td style={styles.td}><input style={styles.inlineInput} value={editTownData.assessor_website} onChange={(e) => setEditTownData({ ...editTownData, assessor_website: e.target.value })} /></td>
                          <td style={styles.td}>
                            <button style={styles.saveButton} onClick={() => handleSaveTown(town.town_code)}>Save</button>
                            <button style={styles.cancelButton} onClick={() => setEditingTown(null)}>Cancel</button>
                          </td>
                        </>
                      ) : (
                        <>
                          <td style={styles.td}>{town.assessor_email || '—'}</td>
                          <td style={styles.td}>{town.assessor_phone || '—'}</td>
                          <td style={styles.td}>{town.assessor_website || '—'}</td>
                          <td style={styles.td}><button style={styles.viewButton} onClick={() => handleEditTown(town)}>Edit</button></td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}

        {/* --- ACTIVITY TAB (admin activity log) --- */}
        {activeTab === 'activity' && isSuperAdmin && <ActivityLog token={token} />}

        {/* --- USER MANAGEMENT TAB --- */}
        {activeTab === 'users' && isSuperAdmin && (
          <>
            <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
              <input style={styles.filterInput} type="text" placeholder="Search by email..." value={userSearch} onChange={(e) => setUserSearch(e.target.value)} />
              {promoteMessage && <span style={{ color: '#2e7d32', fontSize: '14px', fontWeight: '600' }}>{promoteMessage}</span>}
            </div>
            {error && <p style={styles.error}>{error}</p>}
            {usersLoading ? (
              <p style={styles.loading}>Loading users...</p>
            ) : filteredUsers.length === 0 ? (
              <p style={styles.loading}>No users found.</p>
            ) : (
              <table style={styles.table}>
                <thead>
                  <tr>
                    {['Email', 'Created', 'Filings', ''].map(h => (
                      <th key={h} style={styles.th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((user, i) => (
                    <React.Fragment key={user.id}>
                      <tr style={{ backgroundColor: i % 2 === 0 ? '#f8fafc' : '#ffffff' }}>
                        <td style={styles.td}>{user.email}</td>
                        <td style={styles.td}>{user.created_at ? new Date(user.created_at).toLocaleDateString() : '—'}</td>
                        <td style={styles.td}>{user.filing_count}</td>
                        <td style={styles.td}>
                          <button style={styles.viewButton} onClick={() => setPromotingUser(promotingUser === user.id ? null : user.id)}>
                            {promotingUser === user.id ? 'Cancel' : 'Promote'}
                          </button>
                          <button style={styles.deleteSmallButton} onClick={() => handleDeleteUser(user.id, user.email)}>Delete</button>
                        </td>
                      </tr>
                      {promotingUser === user.id && (
                        <tr style={{ backgroundColor: '#fffbea' }}>
                          <td colSpan={4} style={{ padding: '12px' }}>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                              <span style={{ fontSize: '13px', fontWeight: '600', color: '#1a3a5c' }}>Promote to Town Admin:</span>
                              <input
                                style={{ ...styles.inlineInput, minWidth: '100px' }}
                                placeholder="Town code (e.g. 035)"
                                value={promoteData.town_code}
                                onChange={(e) => setPromoteData({ ...promoteData, town_code: e.target.value })}
                              />
                              <input
                                style={{ ...styles.inlineInput, minWidth: '160px' }}
                                type="password"
                                placeholder="Set admin password"
                                value={promoteData.password}
                                onChange={(e) => setPromoteData({ ...promoteData, password: e.target.value })}
                              />
                              <button style={styles.saveButton} onClick={() => handlePromoteUser(user.id)}>Confirm Promote</button>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}

      </div>
    </div>
  );
}

const styles = {
  container: { minHeight: '100vh', backgroundColor: '#f4f6f9', padding: '40px 20px' },
  card: { backgroundColor: '#ffffff', padding: '40px', borderRadius: '8px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)', maxWidth: '1200px', margin: '0 auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' },
  title: { color: '#1a3a5c', fontSize: '24px', margin: 0 },
  logoutButton: { padding: '8px 16px', backgroundColor: 'transparent', border: '1px solid #1a3a5c', color: '#1a3a5c', borderRadius: '4px', cursor: 'pointer', fontSize: '14px' },
  tabRow: { display: 'flex', gap: '8px', marginBottom: '24px', borderBottom: '2px solid #eee', paddingBottom: '0' },
  tab: { padding: '10px 20px', backgroundColor: 'transparent', border: 'none', borderBottom: '2px solid transparent', cursor: 'pointer', fontSize: '14px', color: '#666', marginBottom: '-2px' },
  tabActive: { padding: '10px 20px', backgroundColor: 'transparent', border: 'none', borderBottom: '2px solid #1a3a5c', cursor: 'pointer', fontSize: '14px', color: '#1a3a5c', fontWeight: '600', marginBottom: '-2px' },
  statsRow: { display: 'flex', gap: '16px', marginBottom: '24px', flexWrap: 'wrap' },
  statBox: { backgroundColor: '#f4f6f9', borderRadius: '8px', padding: '16px 24px', textAlign: 'center', minWidth: '120px' },
  statNumber: { fontSize: '32px', fontWeight: '700', color: '#1a3a5c' },
  statLabel: { fontSize: '12px', color: '#666', marginTop: '4px' },
  filterRow: { display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap', alignItems: 'center' },
  filterInput: { padding: '8px 10px', borderRadius: '4px', border: '1px solid #ccc', fontSize: '14px', minWidth: '140px' },
  filterButton: { padding: '8px 16px', backgroundColor: '#1a3a5c', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '14px' },
  clearButton: { padding: '8px 16px', backgroundColor: '#888', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '14px' },
  exportButton: { padding: '8px 16px', backgroundColor: '#2e7d32', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '14px' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '14px' },
  th: { backgroundColor: '#1a3a5c', color: '#ffffff', padding: '10px 12px', textAlign: 'left', fontWeight: '600' },
  td: { padding: '10px 12px', borderBottom: '1px solid #eee', color: '#333' },
  viewButton: { padding: '4px 12px', backgroundColor: '#1a3a5c', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', marginRight: '4px' },
  deleteSmallButton: { padding: '4px 12px', backgroundColor: '#cc0000', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' },
  printButton: { padding: '8px 16px', backgroundColor: '#1a3a5c', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '14px' },
  deleteButton: { padding: '8px 16px', backgroundColor: '#cc0000', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '14px' },
  activeToggle: { padding: '4px 12px', backgroundColor: '#2e7d32', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' },
  inactiveToggle: { padding: '4px 12px', backgroundColor: '#888', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' },
  inlineInput: { width: '100%', padding: '4px 6px', borderRadius: '4px', border: '1px solid #ccc', fontSize: '13px', boxSizing: 'border-box' },
  saveButton: { padding: '4px 12px', backgroundColor: '#2e7d32', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', marginRight: '4px' },
  cancelButton: { padding: '4px 12px', backgroundColor: '#888', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' },
  loading: { textAlign: 'center', color: '#666', padding: '40px' },
  error: { color: '#cc0000', fontSize: '14px', marginBottom: '16px' },
  detailHeader: { marginBottom: '24px' },
  backButton: { padding: '8px 16px', backgroundColor: 'transparent', border: '1px solid #1a3a5c', color: '#1a3a5c', borderRadius: '4px', cursor: 'pointer', fontSize: '14px', marginBottom: '16px' },
  detailSection: { marginBottom: '24px', borderTop: '1px solid #eee', paddingTop: '16px' },
  sectionTitle: { color: '#1a3a5c', fontSize: '15px', marginBottom: '12px' },
  detailGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' },
  detailItem: { fontSize: '14px', color: '#333', padding: '6px 0' },
  itemRow: { fontSize: '14px', color: '#333', padding: '8px 0', borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', gap: '8px' },
  itemBadge: { backgroundColor: '#f4f6f9', border: '1px solid #ddd', borderRadius: '4px', padding: '2px 8px', fontSize: '12px', color: '#555', marginLeft: 'auto' },
};

export default AdminDashboard;